import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useUnitWorkspace from '@/data/hook/unit/useUnitWorkspace';
import {
  createLessonBlock as createUnit,
  updateLessonBlock as updateUnit,
} from '@/data/services/lessonBlocksServices';
import Unit from '@/models/Unit';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';

import UnitForm, { UnitFormFields } from './UnitForm';

export type UnitFormServiceSuccess = {
  item: Unit;
  action: 'create' | 'update';
};

type UnitFormServiceProps = {
  onSuccess?: (success: UnitFormServiceSuccess) => void;
};

export default function UnitFormService({ onSuccess }: UnitFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.unitForm.alerts',
  });
  const { unit, slug, unitId, cache } = useUnitWorkspace();

  const handleCreate = async (data: UnitFormFields): Promise<Unit> => {
    if (!slug) throw new Error('Slug not found');
    const createdUnit = await createUnit(slug, { name: data.name });
    alert.success(t('createSuccess'));
    return createdUnit;
  };

  const handleUpdate = async (data: UnitFormFields): Promise<Unit> => {
    if (!unit || !slug) throw new Error('Unit not found');
    const payload = { name: data.name };
    const changedFields = buildChangedObject(unit, payload);
    if (Object.keys(changedFields).length === 0) return unit;
    const updatedUnit = await updateUnit(slug, unitId, changedFields);
    alert.success(t('updateSuccess'));
    return updatedUnit;
  };

  const handleSubmit = async (
    data: UnitFormFields,
  ): Promise<UnitFormServiceSuccess> => {
    if (!unit) {
      const createdUnit = await handleCreate(data);
      return { item: createdUnit, action: 'create' };
    }
    const updatedUnit = await handleUpdate(data);
    return { item: updatedUnit, action: 'update' };
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <UnitForm
      key={unit?.id}
      unit={unit}
      onSubmit={onSubmit}
      isLoading={isPending}
    />
  );
}
